/* Shut Off Valves finder — guided gate-valve selector.
 * Steps: Brand (Kennedy / Wilkins) -> Size -> Gate type (OS&Y / NRS) -> results.
 * Data-driven from the storefront shutoff-valves catalog. Gate type comes from
 * the product `gate:` tags, exposed by the app API as `product.gates` (["OS&Y"]
 * / ["NRS"]). Results dispatch `quote:add` to the global quote drawer, same as
 * the repair-parts finder. Only Kennedy & Wilkins are offered (per store spec).
 */
if (!customElements.get('shutoff-finder')) {
  // Only these two manufacturers, in this display order.
  const BRANDS = [
    { key: 'kennedy', label: 'Kennedy' },
    { key: 'wilkins', label: 'Wilkins' },
  ];
  const BRAND_KEYS = BRANDS.map((b) => b.key);
  // Gate/stem type — shown on the third step. Order + helper copy.
  const GATES = [
    { key: 'OS&Y', label: 'OS&Y', desc: 'Outside screw & yoke — rising stem shows open/closed at a glance.' },
    { key: 'NRS', label: 'NRS', desc: 'Non-rising stem — compact where headroom is tight.' },
  ];
  const PLACEHOLDER = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v6M12 16v6M2 12h6M16 12h6"/></svg>';

  class ShutoffFinder extends HTMLElement {
    constructor() {
      super();
      this.rows = []; // [{ brandKey, brand, size, gate, product, variant }]
      this.state = { brand: null, size: null, gate: null };
    }

    connectedCallback() {
      this.apiBase = (this.dataset.apiBase || 'https://app.thebackflowdepot.com').replace(/\/$/, '');
      this.brandLogos = { kennedy: (this.dataset.kennedyLogo || '').trim(), wilkins: (this.dataset.wilkinsLogo || '').trim() };
      this.innerHTML = '<p class="shutoff-finder__loading">Loading shut-off valves…</p>';
      this.load();
    }

    async load() {
      try {
        const res = await fetch(`${this.apiBase}/api/storefront/parts-finder/products?scope=shutoff-valves&category=shutoff-valves&limit=100`);
        const data = await res.json();
        this.build(data.products || []);
        this.render();
      } catch (e) {
        this.innerHTML = '<p class="shutoff-finder__loading">Couldn’t load valves. Please call (916) 251-7071 and we’ll help you find the right shut-off valve.</p>';
      }
    }

    // Flatten each Kennedy/Wilkins valve into (size × gate) rows so the funnel
    // can offer sizes for a brand, then gate types for that brand+size.
    build(products) {
      this.rows = [];
      for (const p of products) {
        const brandKey = (p.brand || '').toLowerCase();
        if (!BRAND_KEYS.includes(brandKey)) continue; // Kennedy / Wilkins only
        const sizes = p.sizes || [];
        const gates = p.gates || [];
        if (!sizes.length || !gates.length) continue; // needs both to be findable
        const variant = (p.variants || [])[0];
        if (!variant || !variant.variantId) continue;
        for (const size of sizes) {
          for (const gate of gates) {
            this.rows.push({ brandKey, brand: p.brand, size, gate, product: p, variant });
          }
        }
      }
    }

    brandsAvail() {
      const have = new Set(this.rows.map((r) => r.brandKey));
      return BRANDS.filter((b) => have.has(b.key));
    }
    sizesFor(brandKey) {
      const set = new Set(this.rows.filter((r) => r.brandKey === brandKey).map((r) => r.size));
      return [...set].sort((a, b) => parseFloat(a) - parseFloat(b) || String(a).localeCompare(String(b)));
    }
    gatesFor(brandKey, size) {
      const have = new Set(this.rows.filter((r) => r.brandKey === brandKey && r.size === size).map((r) => r.gate));
      return GATES.filter((g) => have.has(g.key));
    }
    resultsFor(brandKey, size, gate) {
      const seen = new Set();
      const out = [];
      for (const r of this.rows) {
        if (r.brandKey !== brandKey || r.size !== size || r.gate !== gate) continue;
        if (seen.has(r.variant.variantId)) continue;
        seen.add(r.variant.variantId);
        out.push(r);
      }
      return out;
    }

    setState(patch) { Object.assign(this.state, patch); this.render(); }

    sizeLabel(s) { return /^\d*\.?\d+$/.test(s) ? `${s}"` : s; }

    // Valves 2.5" and up ship by freight / aren't stocked in every size, so
    // they're quote-only (no live price) — mirrors the product-page quote gate.
    sizeValue(s) {
      const str = String(s).trim();
      if (str.includes('/')) {
        const parts = str.split(/\s+/);
        if (parts.length >= 2) { const a = parseFloat(parts[0]) || 0; const [n, d] = parts[1].split('/').map(Number); return a + (d ? n / d : 0); }
        const [n, d] = str.split('/').map(Number); return d ? n / d : 0;
      }
      const n = parseFloat(str); return Number.isNaN(n) ? 0 : n;
    }
    isQuoteSize(s) { return this.sizeValue(s) >= 2.5; }

    thumb(image, extraClass) {
      const inner = image ? `<img src="${this.esc(image)}" alt="" loading="lazy">` : PLACEHOLDER;
      return `<span class="shutoff-finder__thumb${extraClass ? ' ' + extraClass : ''}">${inner}</span>`;
    }

    render() {
      const s = this.state;
      let step = 1;
      if (s.brand) step = 2;
      if (s.brand && s.size) step = 3;
      if (s.brand && s.size && s.gate) step = 4;

      const crumb = (n, label, active, done) =>
        `<button type="button" class="shutoff-finder__pill${active ? ' is-active' : ''}${done ? ' is-done' : ''}" data-goto="${n}"${done ? '' : ' disabled'}>${n}. ${label}</button>`;

      let body = '';
      if (step === 1) {
        const brands = this.brandsAvail();
        body = brands.length
          ? `<div class="shutoff-finder__grid">${brands.map((b) => {
              const logo = this.brandLogos[b.key];
              const head = logo
                ? `<img class="shutoff-finder__brand-logo shutoff-finder__brand-logo--${b.key}" src="${this.esc(logo)}" alt="${this.esc(b.label)}" loading="lazy">`
                : `<span class="shutoff-finder__card-title">${b.label}</span>`;
              return `<button type="button" class="shutoff-finder__card" data-brand="${b.key}">${head}<span class="shutoff-finder__card-desc">Gate valves — OS&amp;Y &amp; NRS</span></button>`;
            }).join('')}</div>`
          : `<p class="shutoff-finder__loading">No shut-off valves found. Please call (916) 251-7071.</p>`;
      } else if (step === 2) {
        const sizes = this.sizesFor(s.brand);
        body = `<div class="shutoff-finder__grid shutoff-finder__grid--sizes">${sizes.map((sz) =>
          `<button type="button" class="shutoff-finder__size" data-size="${this.esc(sz)}">${this.sizeLabel(sz)}</button>`
        ).join('')}</div>`;
      } else if (step === 3) {
        const gates = this.gatesFor(s.brand, s.size);
        body = `<div class="shutoff-finder__compare-row">${gates.map((g) =>
          `<button type="button" class="shutoff-finder__compare" data-gate="${this.esc(g.key)}"><span class="shutoff-finder__compare-title">${g.label}</span><span class="shutoff-finder__compare-desc">${g.desc}</span></button>`
        ).join('')}</div>`;
      } else if (step === 4) {
        const results = this.resultsFor(s.brand, s.size, s.gate);
        const brandLabel = (BRANDS.find((b) => b.key === s.brand) || {}).label || '';
        if (!results.length) {
          body = `<div class="shutoff-finder__summary">
            <p class="shutoff-finder__summary-line">No exact match in stock.</p>
            <p class="shutoff-finder__note">We can still source it — <a href="/pages/contact">request a quote</a> or call <strong>(916) 251-7071</strong>.</p>
          </div>`;
        } else {
          body = `<div class="shutoff-finder__results">${results.map((r) => {
            const p = r.product;
            // Kennedy valves ship by freight, so pricing is confirmed per-order
            // (with shipping) rather than shown live. Wilkins keeps its price.
            const isKennedy = r.brandKey === 'kennedy';
            const quoteOnly = isKennedy || this.isQuoteSize(r.size);
            const price = isKennedy
              ? 'Call for freight pricing'
              : quoteOnly
                ? 'Quote on request'
                : (p.priceMin > 0 ? `$${Number(p.priceMin).toFixed(2)}` : 'Pricing on quote');
            const logoUrl = this.brandLogos[r.brandKey];
            const logo = logoUrl
              ? `<img class="shutoff-finder__result-logo shutoff-finder__result-logo--${r.brandKey}" src="${this.esc(logoUrl)}" alt="${this.esc(brandLabel)}">`
              : '';
            const handle = p.handle ? `/products/${this.esc(p.handle)}` : '#';
            return `<div class="shutoff-finder__result">
              ${this.thumb(p.imageUrl)}
              <div class="shutoff-finder__result-body">
                ${logo}
                <a class="shutoff-finder__result-title" href="${handle}">${this.esc(p.title)}</a>
                <p class="shutoff-finder__result-meta">${brandLabel} · ${this.sizeLabel(r.size)} · ${r.gate}</p>
                <p class="shutoff-finder__result-price${quoteOnly ? ' shutoff-finder__result-price--quote' : ''}">${price}</p>
                <div class="shutoff-finder__result-actions">
                  <button type="button" class="btn btn--primary js-add-to-quote" data-variant-id="${this.esc(r.variant.variantId)}" data-title="${this.esc(p.title)}" data-sku="${this.esc(r.variant.sku || '')}" data-image="${this.esc(p.imageUrl || '')}" data-handle="${this.esc(p.handle || '')}">Add to quote</button>
                  <a class="shutoff-finder__view" href="${handle}">View product →</a>
                </div>
              </div>
            </div>`;
          }).join('')}</div>`;
        }
      }

      this.innerHTML = `
        <div class="shutoff-finder__steps">
          ${crumb(1, 'Brand', step === 1, true)}
          ${crumb(2, 'Size', step === 2, !!s.brand)}
          ${crumb(3, 'Gate type', step === 3, !!s.size)}
          ${crumb(4, 'Valve', step === 4, !!s.gate)}
        </div>
        <div class="shutoff-finder__panel">${body}</div>`;
      this.bind();
    }

    bind() {
      this.querySelectorAll('[data-brand]').forEach((el) => el.addEventListener('click', () => this.setState({ brand: el.dataset.brand, size: null, gate: null })));
      this.querySelectorAll('[data-size]').forEach((el) => el.addEventListener('click', () => this.setState({ size: el.dataset.size, gate: null })));
      this.querySelectorAll('[data-gate]').forEach((el) => el.addEventListener('click', () => this.setState({ gate: el.dataset.gate })));
      this.querySelectorAll('[data-goto]:not([disabled])').forEach((el) => el.addEventListener('click', () => {
        const n = Number(el.dataset.goto);
        if (n === 1) this.setState({ brand: null, size: null, gate: null });
        else if (n === 2) this.setState({ size: null, gate: null });
        else if (n === 3) this.setState({ gate: null });
      }));
      // Results add-to-quote → global quote drawer (same contract as the finder).
      this.querySelectorAll('.js-add-to-quote').forEach((btn) => btn.addEventListener('click', (event) => {
        event.preventDefault();
        document.dispatchEvent(new CustomEvent('quote:add', { detail: {
          variantId: btn.dataset.variantId, title: btn.dataset.title,
          sku: btn.dataset.sku || null, image: btn.dataset.image || null,
          handle: btn.dataset.handle || null, quantity: 1,
        } }));
      }));
    }

    esc(s) { return String(s).replace(/"/g, '&quot;').replace(/</g, '&lt;'); }
  }
  customElements.define('shutoff-finder', ShutoffFinder);
}
